// The phone-side store behind every cache in the app, kept in IndexedDB so a
// download happens once: country outlines (lib/outlines.ts) and the tiered
// data cache (lib/dataCache.ts). Every record carries the data version it was written under;
// bumping the version hides every older record and removes it lazily, which
// is the invalidation these assets want (an outline changes with a Natural
// Earth release, never with time). Without IndexedDB (private windows, jsdom)
// a per-session Map stands in, so callers never have to care.

/** Bump when druif imports a new Natural Earth release; every cached outline is then refetched. */
export const OUTLINE_DATA_VERSION = "ne-5.1.2";

const DB_NAME = "proef-assets";
const DB_VERSION = 1;
const STORE = "assets";

export interface AssetRow {
  key: string;
  version: string;
  value: unknown;
}

type Guard<T> = (value: unknown) => value is T;

const memory = new Map<string, AssetRow>();

function isAssetRow(value: unknown): value is AssetRow {
  return (
    typeof value === "object" &&
    value !== null &&
    "key" in value &&
    "version" in value &&
    "value" in value &&
    typeof value.version === "string"
  );
}

function openDatabase(): Promise<IDBDatabase | undefined> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") {
        resolve(undefined);
        return;
      }
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.addEventListener("upgradeneeded", () => {
        if (!request.result.objectStoreNames.contains(STORE)) {
          request.result.createObjectStore(STORE, { keyPath: "key" });
        }
      });
      request.addEventListener("success", () => {
        resolve(request.result);
      });
      request.addEventListener("error", () => {
        resolve(undefined);
      });
      request.addEventListener("blocked", () => {
        resolve(undefined);
      });
    } catch {
      resolve(undefined);
    }
  });
}

function settle<T>(request: IDBRequest<T>): Promise<T | undefined> {
  return new Promise((resolve) => {
    request.addEventListener("success", () => {
      resolve(request.result);
    });
    request.addEventListener("error", () => {
      resolve(undefined);
    });
  });
}

/** The value stored under `key` for exactly this data version, when it passes `is`. */
export async function readAsset<T>(
  key: string,
  version: string,
  is: Guard<T>,
): Promise<T | undefined> {
  const db = await openDatabase();
  if (db === undefined) {
    const hit = memory.get(key);
    return hit !== undefined && hit.version === version && is(hit.value) ? hit.value : undefined;
  }
  try {
    const row: unknown = await settle(
      db.transaction(STORE, "readonly").objectStore(STORE).get(key),
    );
    if (!isAssetRow(row)) return undefined;
    if (row.version !== version) {
      // An older data version: gone on the next write to this key, and
      // invisible until then.
      await settle(db.transaction(STORE, "readwrite").objectStore(STORE).delete(key));
      return undefined;
    }
    return is(row.value) ? row.value : undefined;
  } catch {
    return undefined;
  } finally {
    db.close();
  }
}

/** Stores `value` under `key` for this data version; best effort, never throws. */
export async function writeAsset(key: string, version: string, value: unknown): Promise<void> {
  const row: AssetRow = { key, version, value };
  const db = await openDatabase();
  if (db === undefined) {
    memory.set(key, row);
    return;
  }
  try {
    await settle(db.transaction(STORE, "readwrite").objectStore(STORE).put(row));
  } catch {
    // A full or blocked store only costs a refetch next time.
  } finally {
    db.close();
  }
}

/** Drops every asset whose key starts with `prefix`, whatever its version. */
export function removeAssetsWithPrefix(prefix: string): Promise<void> {
  return removeAssetsWhere((row) => row.key.startsWith(prefix));
}

/**
 * Drops every asset `matches` picks. Walks the store with a cursor so a
 * sweep over megabytes of outlines never loads them into memory; a failed
 * sweep only means a stale entry is served once more.
 */
export async function removeAssetsWhere(matches: (row: AssetRow) => boolean): Promise<void> {
  for (const [key, row] of memory) if (matches(row)) memory.delete(key);
  const db = await openDatabase();
  if (db === undefined) return;
  try {
    await new Promise<void>((resolve) => {
      const request = db.transaction(STORE, "readwrite").objectStore(STORE).openCursor();
      request.addEventListener("success", () => {
        const cursor = request.result;
        if (cursor === null) {
          resolve();
          return;
        }
        const row: unknown = cursor.value;
        if (isAssetRow(row) && matches(row)) cursor.delete();
        cursor.continue();
      });
      request.addEventListener("error", () => {
        resolve();
      });
    });
  } catch {
    // See above: the next read of a survivor is still version-checked.
  } finally {
    db.close();
  }
}

/** The rows whose key starts with `prefix`: memory first, then the store walked with a cursor. */
async function rowsWithPrefix(prefix: string): Promise<AssetRow[]> {
  const found = new Map<string, AssetRow>();
  for (const row of memory.values()) if (row.key.startsWith(prefix)) found.set(row.key, row);
  const db = await openDatabase();
  if (db === undefined) return [...found.values()];
  try {
    await new Promise<void>((resolve) => {
      const request = db.transaction(STORE, "readonly").objectStore(STORE).openCursor();
      request.addEventListener("success", () => {
        const cursor = request.result;
        if (cursor === null) {
          resolve();
          return;
        }
        const row: unknown = cursor.value;
        if (isAssetRow(row) && row.key.startsWith(prefix)) found.set(row.key, row);
        cursor.continue();
      });
      request.addEventListener("error", () => {
        resolve();
      });
    });
  } catch {
    // A failed walk only means fewer rows are found.
  } finally {
    db.close();
  }
  return [...found.values()];
}

export async function hasAssetsWithPrefix(prefix: string): Promise<boolean> {
  return (await rowsWithPrefix(prefix)).length > 0;
}

/**
 * Renames every asset under `from` to the same key under `to`, versions and values untouched: how a
 * device sets one account's local Academy data aside, and gives it back, without reading it.
 */
export async function moveAssets(from: string, to: string): Promise<void> {
  const rows = await rowsWithPrefix(from);
  for (const row of rows) await writeAsset(to + row.key.slice(from.length), row.version, row.value);
  await removeAssetsWhere((row) => rows.some((moved) => moved.key === row.key));
}

/** Drops every cached asset, whatever its version. */
export async function clearAssetCache(): Promise<void> {
  memory.clear();
  const db = await openDatabase();
  if (db === undefined) return;
  try {
    await settle(db.transaction(STORE, "readwrite").objectStore(STORE).clear());
  } catch {
    // Nothing to clear is fine too.
  } finally {
    db.close();
  }
}
